import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react';

import type { CalculatorDto } from '@/lib/api/tools';

import { sectionAnchorFromLegacy } from './useCostCalculatorState';

export const RESULTS_ANCHOR_ID = 'calculator-results';

export type UseCostCalculatorClientOptions = {
  calculator: CalculatorDto;
  recomputeResults: () => void;
  setExpandedSections: Dispatch<SetStateAction<Record<string, boolean>>>;
  setShowCalculator: Dispatch<SetStateAction<boolean>>;
};

export const useCostCalculatorClient = ({
  calculator,
  recomputeResults,
  setExpandedSections,
  setShowCalculator
}: UseCostCalculatorClientOptions) => {
  const sectionsAnchorRef = useRef<HTMLDivElement | null>(null);

  const scrollToResults = useCallback(() => {
    const el = document.getElementById(RESULTS_ANCHOR_ID);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleGetStarted = useCallback(() => {
    setShowCalculator(true);
    const el = sectionsAnchorRef.current;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [setShowCalculator]);

  const handleToggleSection = useCallback(
    (legacyId: string) => {
      setExpandedSections((prev) => ({ ...prev, [legacyId]: !prev[legacyId] }));
    },
    [setExpandedSections]
  );

  const handleExpandNext = useCallback(
    (currentIndex: number) => {
      const next = calculator.sections[currentIndex + 1];
      if (!next) return;
      setExpandedSections((prev) => ({ ...prev, [next.legacyId]: true }));
      const anchor = sectionAnchorFromLegacy(next.legacyId, next.title);
      if (!anchor) return;
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [calculator.sections, setExpandedSections]
  );

  const handleCalculateAndScroll = useCallback(() => {
    recomputeResults();
    scrollToResults();
  }, [recomputeResults, scrollToResults]);

  return {
    sectionsAnchorRef,
    scrollToResults,
    handleGetStarted,
    handleToggleSection,
    handleExpandNext,
    handleCalculateAndScroll
  };
};

export default useCostCalculatorClient;
